import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import { useAuth } from '../contexts/AuthContext';
import { getApiUrl, API_ENDPOINTS } from '../config/api';
import { useTranslation } from 'react-i18next';

interface AnnouncementMessage {
  titleLt?: string;
  bodyLt?: string;
  titleEn?: string;
  bodyEn?: string;
}

interface AnnouncementPayload {
  campaignId: string;
  year: number;
  month: number;
  closedAt?: string;
  source?: string;
  messageVersion?: number;
  message?: AnnouncementMessage;
}

const MONTH_KEYS = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december'
] as const;

interface SchedulingCloseAnnouncementModalProps {
  onNavigateToSchedule?: () => void;
}

const SchedulingCloseAnnouncementModal: React.FC<SchedulingCloseAnnouncementModalProps> = ({
  onNavigateToSchedule
}) => {
  const { token, user } = useAuth();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [announcement, setAnnouncement] = useState<AnnouncementPayload | null>(null);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    if (!token || !user) {
      setOpen(false);
      setAnnouncement(null);
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const response = await fetch(getApiUrl(API_ENDPOINTS.SCHEDULING_CLOSE_ANNOUNCEMENT), {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!response.ok) return;
        const data = await response.json();
        if (cancelled) return;
        if (data.shouldShow && data.announcement) {
          setAnnouncement(data.announcement);
          setOpen(true);
        }
      } catch (err) {
        console.error('Failed to load scheduling close announcement', err);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [token, user?.id]);

  const monthLabel =
    announcement != null
      ? `${t(`common.${MONTH_KEYS[announcement.month - 1]}`, String(announcement.month))} ${announcement.year}`
      : '';

  const isEnglish = (i18n.language || '').toLowerCase().startsWith('en');
  const announcementTitle = isEnglish
    ? announcement?.message?.titleEn ||
      t('common.schedulingCloseAnnouncementTitle', 'Automatic approval is turned off')
    : announcement?.message?.titleLt ||
      t('common.schedulingCloseAnnouncementTitle', 'Automatinis patvirtinimas išjungtas');
  const announcementBody = isEnglish
    ? announcement?.message?.bodyEn ||
      t(
        'common.schedulingCloseAnnouncementBody',
        'Automatic schedule approval for this month is turned off. All of your work schedule changes will wait for admin or manager approval.'
      )
    : announcement?.message?.bodyLt ||
      t(
        'common.schedulingCloseAnnouncementBody',
        'Šiam mėnesiui automatinis grafiko patvirtinimas išjungtas. Visi jūsų darbo grafiko pakeitimai lauks administratoriaus arba vadovo patvirtinimo.'
      );

  const handleDismiss = async () => {
    if (!token) {
      setOpen(false);
      return;
    }
    try {
      setDismissing(true);
      await fetch(getApiUrl(API_ENDPOINTS.SCHEDULING_CLOSE_ANNOUNCEMENT_DISMISS), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: '{}'
      });
    } catch (err) {
      console.error('Failed to dismiss announcement', err);
    } finally {
      setDismissing(false);
      setOpen(false);
    }
  };

  const handleGoToSchedule = () => {
    handleDismiss();
    onNavigateToSchedule?.();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        handleDismiss();
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          background: `linear-gradient(165deg,
            ${alpha(theme.palette.background.paper, 0.92)} 0%,
            ${alpha(theme.palette.primary.light, 0.12)} 100%)`,
          backdropFilter: 'blur(16px)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
          boxShadow: `0 24px 48px ${alpha(theme.palette.common.black, 0.18)}`
        }
      }}
    >
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 1,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2
        }}
      >
        <Box
          sx={{
            width: 52,
            height: 52,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(135deg, ${theme.palette.warning.light}, ${theme.palette.warning.main})`,
            color: theme.palette.warning.contrastText,
            flexShrink: 0
          }}
        >
          <EventBusyIcon />
        </Box>
        <Box>
          <Typography variant="h5" fontWeight={700} gutterBottom>
            {announcementTitle}
          </Typography>
          <Typography variant="subtitle1" color="primary" fontWeight={600}>
            {monthLabel}
          </Typography>
        </Box>
      </Box>
      <DialogContent sx={{ pt: 1 }}>
        <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
          {announcementBody}
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 1, flexWrap: 'wrap' }}>
        {onNavigateToSchedule && (
          <Button onClick={handleGoToSchedule} disabled={dismissing} color="inherit">
            {t('common.goToWorkSchedule', 'Darbo grafikas')}
          </Button>
        )}
        <Box flexGrow={1} />
        <Button
          variant="contained"
          onClick={handleDismiss}
          disabled={dismissing}
          sx={{ minWidth: 120, fontWeight: 700 }}
        >
          {t('common.understood', 'Supratau')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SchedulingCloseAnnouncementModal;
